import { Fragment, useEffect, useState, type ReactNode } from "react";
import { NavLink } from "react-router-dom";
import {
  Box,
  Divider,
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  Toolbar,
  Tooltip,
  Typography,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import {
  AssignmentOutlined,
  ChevronLeft,
  ChevronRight,
  FilterAltOutlined,
  PersonOutline,
  ApartmentOutlined,
  CreateNewFolderOutlined,
  Payment,
  PhoneOutlined,
  WorkOutline,
  AutoFixHigh,
  Settings,
  PostAdd,
  AssessmentOutlined,
  PeopleOutline,
  GavelOutlined,
} from "@mui/icons-material";
import clipboardIcon from "@/assets/svg/clipboard.svg";
import trendIcon from "@/assets/svg/trend.svg";
import type { NavIconKey } from "@/types/tenantFeatures";
import { useTenantFeatures } from "@/context/TenantFeaturesContext";
import { SidebarUserMenu } from "./SidebarUserMenu";
import badgerLogo from "../../assets/image.png";

const DRAWER_WIDTH = 200;
const COLLAPSED_WIDTH = 60;
const SIDEBAR_COLLAPSED_KEY = "sidebar-collapsed";
const sidebarBg = "#182737";
const sidebarSvgIconSx = {
  width: 20,
  height: 20,
  display: "block",
  flexShrink: 0,
  filter: "brightness(0) invert(1)",
};

function readCollapsedPreference(): boolean {
  try {
    return localStorage.getItem(SIDEBAR_COLLAPSED_KEY) === "true";
  } catch {
    return false;
  }
}

function renderNavIcon(iconKey: NavIconKey): ReactNode {
  switch (iconKey) {
    case "trend":
      return (
        <Box
          component="img"
          src={trendIcon}
          alt=""
          aria-hidden
          sx={sidebarSvgIconSx}
        />
      );
    case "clipboard":
      return (
        <Box
          component="img"
          src={clipboardIcon}
          alt=""
          aria-hidden
          sx={sidebarSvgIconSx}
        />
      );
    case "myTasks":
      return <AssignmentOutlined />;
    case "pipeline":
      return <FilterAltOutlined />;
    case "create":
      return <CreateNewFolderOutlined />;
    case "aiExtractions":
      return <AutoFixHigh />;
    case "sponsors":
      return <PersonOutline />;
    case "ndas":
      return <GavelOutlined />;
    case "contacts":
      return <PhoneOutlined />;
    case "sources":
      return <WorkOutline />;
    case "marketReports":
      return <AssessmentOutlined />;
    case "assets":
      return <ApartmentOutlined />;
    case "tenants":
      return <Payment />;
    case "prompts":
      return <Settings />;
    case "templates":
      return <PostAdd />;
    case "users":
      return <PeopleOutline />;
    default:
      return null;
  }
}

const SIDEBAR_APP_NAME = "Badger";

export const Sidebar = () => {
  const { navigation: navSections } = useTenantFeatures();
  const [collapsed, setCollapsed] = useState(readCollapsedPreference);
  const sidebarWidth = collapsed ? COLLAPSED_WIDTH : DRAWER_WIDTH;

  useEffect(() => {
    try {
      localStorage.setItem(SIDEBAR_COLLAPSED_KEY, String(collapsed));
    } catch {
      // ignore storage errors
    }
  }, [collapsed]);

  return (
    <Drawer
      variant="permanent"
      sx={{
        width: sidebarWidth,
        flexShrink: 0,
        transition: (theme) =>
          theme.transitions.create("width", {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.enteringScreen,
          }),
        "& .MuiDrawer-paper": {
          width: sidebarWidth,
          boxSizing: "border-box",
          overflowX: "hidden",
          display: "flex",
          flexDirection: "column",
          position: "relative",
          backgroundColor: sidebarBg,
          borderRight: `1px solid ${alpha("#fff", 0.12)}`,
          color: "#fff",
          transition: (theme) =>
            theme.transitions.create("width", {
              easing: theme.transitions.easing.sharp,
              duration: theme.transitions.duration.enteringScreen,
            }),
        },
      }}
    >
      <Box
        component="button"
        type="button"
        onClick={() => setCollapsed((prev) => !prev)}
        aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
        sx={{
          position: "absolute",
          top: 0,
          right: 0,
          width: 8,
          height: "100%",
          border: "none",
          padding: 0,
          cursor: "pointer",
          zIndex: 2,
          backgroundColor: "transparent",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: alpha("#fff", 0.45),
          "&:hover": {
            backgroundColor: alpha("#fff", 0.1),
            color: alpha("#fff", 0.85),
          },
        }}
      >
        {collapsed ? (
          <ChevronRight sx={{ fontSize: 16, pointerEvents: "none" }} />
        ) : (
          <ChevronLeft sx={{ fontSize: 16, pointerEvents: "none" }} />
        )}
      </Box>

      <Toolbar
        disableGutters
        sx={{
          minHeight: collapsed ? "52px !important" : "72px !important",
          px: collapsed ? 0.5 : 1.5,
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <Box
          component={NavLink}
          to="/"
          aria-label={`${SIDEBAR_APP_NAME} home`}
          sx={{
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            gap: collapsed ? 0 : 1,
            px: collapsed ? 0.375 : 1.5,
            py: collapsed ? 0.375 : 0.75,
            borderRadius: collapsed ? 1 : 9999,
            backgroundColor: "#ffffff",
            textDecoration: "none",
            boxShadow: "0 1px 2px rgba(0,0,0,0.1)",
            maxWidth: "100%",
          }}
        >
          <Box
            component="img"
            src={badgerLogo}
            alt=""
            sx={{
              ...(collapsed
                ? {
                    maxHeight: 24,
                    maxWidth: 32,
                    width: "auto",
                    height: "auto",
                    objectFit: "contain",
                  }
                : {
                    height: 32,
                    width: "auto",
                  }),
              display: "block",
              flexShrink: 0,
            }}
          />
          {!collapsed && (
            <Typography
              component="span"
              variant="body2"
              sx={{
                color: sidebarBg,
                fontWeight: 700,
                fontSize: "0.85rem",
                letterSpacing: "0.04em",
                lineHeight: 1,
                textTransform: "uppercase",
              }}
            >
              {SIDEBAR_APP_NAME}
            </Typography>
          )}
        </Box>
      </Toolbar>

      <Divider sx={{ borderColor: alpha("#fff", 0.08), mx: collapsed ? 0.5 : 1.5, mb: 0.5 }} />

      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          overflowY: "auto",
          overflowX: "hidden",
        }}
      >
        {navSections.map((section, sectionIndex) => (
          <Box key={section.title}>
            {sectionIndex > 0 && (
              <Divider
                sx={{
                  borderColor: alpha("#fff", 0.08),
                  mx: collapsed ? 0.5 : 1.5,
                  my: 0.5,
                }}
              />
            )}
            <List
              dense
              sx={{
                pt: sectionIndex === 0 ? (collapsed ? 0.75 : 1) : 0.5,
                pb: 0.5,
                ...(collapsed && {
                  px: 0.5,
                  width: "100%",
                  boxSizing: "border-box",
                }),
              }}
              subheader={
                collapsed ? undefined : (
                  <Box
                    component="div"
                    sx={{
                      color: "#ffffff",
                      opacity: 0.75,
                      fontWeight: 600,
                      fontSize: "0.7rem",
                      textTransform: "uppercase",
                      letterSpacing: "0.08em",
                      px: 2.5,
                      pt: 1,
                      pb: 0.75,
                      lineHeight: 1.4,
                    }}
                  >
                    {section.title}
                  </Box>
                )
              }
            >
              {section.items.map(({ label, path, iconKey, badge }) => {
                const navItem = (
                  <ListItem
                    component={NavLink}
                    to={path}
                    end={path === "/"}
                    disablePadding={collapsed}
                    sx={{
                      position: "relative",
                      mx: collapsed ? 0 : 1,
                      my: 0.25,
                      px: collapsed ? 0.5 : 1.5,
                      py: collapsed ? 0.75 : 0.75,
                      borderRadius: 1.25,
                      color: "#ffffff !important",
                      justifyContent: collapsed ? "center" : "flex-start",
                      width: collapsed ? "100%" : undefined,
                      maxWidth: collapsed ? "100%" : undefined,
                      boxSizing: "border-box",
                      minWidth: collapsed ? 0 : undefined,
                      transition: "background-color 160ms ease",
                      textDecoration: "none",
                      "& .MuiListItemIcon-root": {
                        color: "#ffffff",
                        minWidth: 0,
                        mr: collapsed ? 0 : 1.5,
                        justifyContent: "center",
                        "& .MuiSvgIcon-root": {
                          fontSize: 20,
                        },
                      },
                      "& .sidebar-nav-label": {
                        color: "#ffffff",
                        fontSize: "0.75rem",
                        fontWeight: 400,
                        letterSpacing: "0.01em",
                        lineHeight: 1.2,
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        display: "block",
                      },
                      "& .sidebar-nav-label-wrap": {
                        position: "relative",
                        display: collapsed ? "none" : "inline-block",
                        width: "fit-content",
                        maxWidth: "100%",
                        flex: "0 1 auto",
                        minWidth: 0,
                      },
                      "&:hover": {
                        backgroundColor: alpha("#fff", 0.12),
                      },
                      "&.active": {
                        backgroundColor: (theme) =>
                          alpha(theme.palette.secondary.main, 0.3),
                        "&::before": collapsed
                          ? { display: "none" }
                          : {
                              content: '""',
                              position: "absolute",
                              left: -4,
                              top: 6,
                              bottom: 6,
                              width: 3,
                              borderRadius: 2,
                              backgroundColor: (theme) =>
                                theme.palette.secondary.main,
                            },
                        "& .sidebar-nav-label": {
                          fontWeight: 600,
                        },
                      },
                    }}
                  >
                    <ListItemIcon>{renderNavIcon(iconKey)}</ListItemIcon>
                    {!collapsed && (
                      <Box component="span" className="sidebar-nav-label-wrap">
                        <Box component="span" className="sidebar-nav-label">
                          {label}
                        </Box>
                        {badge ? (
                          <Box
                            component="span"
                            aria-hidden
                            sx={{
                              position: "absolute",
                              top: -3,
                              right: 0,
                              transform: "translateX(100%)",
                              fontSize: "0.55rem",
                              fontWeight: 600,
                              textTransform: "uppercase",
                              letterSpacing: "0.04em",
                              px: 0.5,
                              py: 0.125,
                              borderRadius: 0.5,
                              backgroundColor: alpha("#fff", 0.18),
                              color: "#fff",
                              lineHeight: 1.2,
                              pointerEvents: "none",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {badge}
                          </Box>
                        ) : null}
                      </Box>
                    )}
                  </ListItem>
                );

                return (
                  <Fragment key={path}>
                    {collapsed ? (
                      <Tooltip
                        title={badge ? `${label} (${badge})` : label}
                        placement="right"
                        arrow
                      >
                        <Box
                          component="span"
                          sx={{
                            display: "block",
                            width: "100%",
                            maxWidth: "100%",
                            boxSizing: "border-box",
                          }}
                        >
                          {navItem}
                        </Box>
                      </Tooltip>
                    ) : (
                      navItem
                    )}
                  </Fragment>
                );
              })}
            </List>
          </Box>
        ))}
      </Box>

      <SidebarUserMenu collapsed={collapsed} />
    </Drawer>
  );
};
